import { useCallback, useEffect, useMemo } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { fg } from '@confluence/feature-gating';

import type {
	useFabricEligibilityMigrationQuery$data,
	useFabricEligibilityMigrationQuery$variables,
} from './__generated__/useFabricEligibilityMigrationQuery.graphql.ts';
import type {
	useFabricEligibilityMigrationQuery_content$data,
	useFabricEligibilityMigrationQuery_content$key,
} from './__generated__/useFabricEligibilityMigrationQuery_content.graphql.ts';

export type FabricEligibilityFragmentRef = useFabricEligibilityMigrationQuery_content$key;
export type FabricEligibilityFragmentData = useFabricEligibilityMigrationQuery_content$data | null;

const UseFabricEligibilityMigrationQuery = graphql`
	query useFabricEligibilityMigrationQuery($contentId: ID!) {
		content(id: $contentId) {
			nodes {
				...useFabricEligibilityMigrationQuery_content @relay(mask: false)
			}
		}
	}
`;

export const UseFabricEligibilityMigrationFragment: GraphQLTaggedNode = graphql`
	fragment useFabricEligibilityMigrationQuery_content on Content {
		id
		metadata {
			frontend {
				fabricEditorEligibility
				contentHash
			}
		}
	}
`;

export const useFabricEligibilityMigrationQuery = ({
	contentId,
	isFabricSupported,
	contentFragmentData,
}: {
	contentId: string;
	isFabricSupported?: boolean;
	contentFragmentData?: FabricEligibilityFragmentData;
}): {
	fabricEditorEligibility: string | null | undefined;
	contentHash: string | null | undefined;
	isFabricEligibilityLoading: boolean;
} => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const shouldFetch = isFabricSupported === false;
	const shouldUseFragment = Boolean(
		contentFragmentData && fg('confluence_vp_fabric_eligibility_frag_reader'),
	);
	const { data, error, loading } = useQueryInterop<
		useFabricEligibilityMigrationQuery$data,
		useFabricEligibilityMigrationQuery$variables
	>(UseFabricEligibilityMigrationQuery, {
		fetchPolicy: 'cache-first',
		skip: shouldUseFragment,
		variables: { contentId },
	});

	const fireErrorEvent = useCallback(
		({ reason }: { reason: 'PROMISE_REJECTED' | 'APOLLO_ERROR' }) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'failed',
					actionSubject: 'fabricEditorEligibilityQuery',
					attributes: {
						contentId,
						reason,
					},
					source: 'editContentButton',
				},
			}).fire();
		},
		[contentId, createAnalyticsEvent],
	);

	useEffect(() => {
		if (error) {
			fireErrorEvent({ reason: 'APOLLO_ERROR' });
		}
	}, [error, fireErrorEvent]);

	return useMemo(() => {
		if (!shouldFetch) {
			return {
				fabricEditorEligibility: null,
				contentHash: null,
				isFabricEligibilityLoading: false,
			};
		}

		if (shouldUseFragment) {
			const metadataFrontend = contentFragmentData?.metadata?.frontend;
			return {
				fabricEditorEligibility: metadataFrontend?.fabricEditorEligibility,
				contentHash: metadataFrontend?.contentHash,
				isFabricEligibilityLoading: false,
			};
		}

		if (!data && shouldFetch) {
			return {
				isFabricEligibilityLoading: true,
				fabricEditorEligibility: null,
				contentHash: null,
			};
		}

		if (!data && loading) {
			return {
				isFabricEligibilityLoading: true,
				fabricEditorEligibility: null,
				contentHash: null,
			};
		}

		if (data) {
			const metadataFrontend = data?.content?.nodes?.[0]?.metadata?.frontend;
			const fabricEditorEligibility = metadataFrontend?.fabricEditorEligibility;
			const contentHash = metadataFrontend?.contentHash;
			return {
				fabricEditorEligibility,
				contentHash,
				isFabricEligibilityLoading: false,
			};
		}
		return {
			fabricEditorEligibility: null,
			contentHash: null,
			isFabricEligibilityLoading: false,
		};
	}, [contentFragmentData, data, loading, shouldFetch, shouldUseFragment]);
};
